import { RefObject, useEffect, useRef } from 'react';

const GUARD = '__noopClickableGuard' as const;

type GuardedHTMLElement = HTMLElement & { [GUARD]?: boolean };

const guardOnClick = (el: GuardedHTMLElement) => {
  if (el[GUARD]) return;
  el[GUARD] = true;

  if (el.onclick) el.onclick = null;

  Object.defineProperty(el, 'onclick', {
    configurable: true,
    enumerable: true,
    get: () => null,
    set: () => {},
  });
};

export const guardNoopClickableElement = (el: HTMLElement) => {
  guardOnClick(el);
};

const processSubtree = (node: Node) => {
  if (!(node instanceof HTMLElement)) return;

  if (node.onclick) guardOnClick(node);

  node.querySelectorAll<HTMLElement>('*').forEach(el => {
    if (el.onclick) guardOnClick(el);
  });
};

const useNoopClickableGuard = <T extends HTMLElement>(): RefObject<T> => {
  const rootRef = useRef<T>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    processSubtree(root);

    const observer = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach(processSubtree);
      }
    });
    observer.observe(root, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  return rootRef;
};

export default useNoopClickableGuard;
